import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bool, func, string, object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Modal } from 'antd';
import { selectPathwayWithRules } from '@pathways/redux/pathways/reducers';
import { fetchPathways } from '@pathways/redux/pathways/actions';
import PathwayStagesTable from '@components/PathwayStagesTable';
import { fetchDocuments } from '@redux/documents/actions';
import { selectDocuments } from '@redux/documents/reducers';
import { fetchForms } from '@redux/forms/actions';
import { selectForms } from '@redux/forms/reducers';
import { fetchContentMessages } from '@redux/contentMessages/actions';
import { selectAllContentMessages } from '@redux/contentMessages/selectors';
import { useRuleContentRefs } from '@pathways/hooks/useRuleContentRefs';
import { Card } from '../../../../cards/Card';
import createColumns from './createColumns';
import createDataSource, { computeStagesFromJourneyEntries } from './createDataSource';
import './style.less';

function PathwayStagesTableCard({
  currentStageSlug,
  pathwayId,
  triggeredRules,
  transitionPathwayToStage,
  triggerAdhocRule,
  loading: loadingProp,
  history,
  match,
  sharedPathway,
  journey,
  includeDeletedStages,
  appUser,
  roleRulesMap,
  ...props
}) {
  const dispatch = useDispatch();
  const [pathwaysLoading, selectorPathway] = useSelector(state =>
    selectPathwayWithRules(state, pathwayId, includeDeletedStages),
  );
  const [pathway, setPathway] = useState(selectorPathway);
  const [filters, setFilters] = useState({});
  const { t } = useTranslation();
  const activePathwayId = pathway?.id;

  useEffect(() => {
    if (sharedPathway?.id) setPathway(sharedPathway);
  }, [sharedPathway?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectorPathway?.id) setPathway(selectorPathway);
  }, [selectorPathway?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(fetchDocuments());
    dispatch(fetchForms());
    dispatch(fetchContentMessages());
    dispatch(fetchPathways());
  }, [dispatch]);

  const [documentsLoading] = useSelector(selectDocuments);
  const [formsLoading] = useSelector(selectForms);
  const [cannedMessagesLoading] = useSelector(selectAllContentMessages);
  const pathwayRules = useMemo(
    () => pathway?.stages?.flatMap(stage => stage.rules || []) || [],
    [pathway],
  );
  const { rules: hydratedPathwayRules } = useRuleContentRefs(pathwayRules);
  const hydratedRuleById = useMemo(
    () => new Map(hydratedPathwayRules.map(rule => [rule.id, rule])),
    [hydratedPathwayRules],
  );
  const hydratedStages = useMemo(
    () =>
      pathway?.stages?.map(stage => ({
        ...stage,
        rules: stage.rules.map(rule => hydratedRuleById.get(rule.id) || rule),
      })),
    [pathway, hydratedRuleById],
  );

  const stagesIncludingDeleted = journey
    ? computeStagesFromJourneyEntries(hydratedStages ?? [], journey.entries ?? [])
    : hydratedStages;

  const [columns, dataSource] = useMemo(
    () => [
      createColumns(
        t,
        stagesIncludingDeleted,
        currentStageSlug,
        triggeredRules,
        triggerAdhocRule,
        activePathwayId,
        filters,
        setFilters,
        roleRulesMap,
        appUser?.relationship?.is_cancelled,
      ),
      createDataSource(hydratedStages, journey),
    ],
    [
      currentStageSlug,
      stagesIncludingDeleted,
      activePathwayId,
      hydratedStages,
      t,
      triggeredRules,
      triggerAdhocRule,
      filters,
      journey,
      roleRulesMap,
      appUser,
    ],
  );

  const { currentStageIndex, transitionDisabled } = useMemo(() => {
    const currentStageIndex =
      pathway?.stages?.findIndex(({ slug }) => slug === currentStageSlug) || 0;

    return {
      currentStageIndex,
      // - 2 is because all pathways have one stage that is for ad-hoc rules
      transitionDisabled:
        currentStageIndex >= pathway?.stages?.length - 2 || appUser?.relationship?.is_cancelled,
    };
  }, [currentStageSlug, pathway, appUser]);

  const transitionStage = useCallback(() => {
    const nextStage = pathway.stages[currentStageIndex + 1];
    Modal.confirm({
      okText: t('cards:PathwayStagesTable.buttons.transition'),
      title: t('cards:PathwayStagesTable.modals.transition', { stageName: nextStage.name }),
      onOk: () => {
        transitionPathwayToStage(nextStage.slug);
      },
    });
  }, [currentStageIndex, pathway, t, transitionPathwayToStage]);

  const loading =
    documentsLoading ||
    formsLoading ||
    cannedMessagesLoading ||
    pathwaysLoading ||
    loadingProp ||
    !pathway;

  return (
    <Card.Full
      className="pathway-stages-table-card"
      extra={
        transitionPathwayToStage ? (
          <Button.Group>
            <Button disabled={transitionDisabled} onClick={transitionStage}>
              {t('cards:PathwayStagesTable.buttons.transition')} <Icon type="right" />
            </Button>
          </Button.Group>
        ) : null
      }
      title={t('cards:PathwayStagesTable.title')}
      {...props}
    >
      <PathwayStagesTable
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        onChange={(_, filters) => setFilters(filters)}
      />
    </Card.Full>
  );
}

PathwayStagesTableCard.propTypes = {
  currentStageSlug: string,
  transitionPathwayToStage: func,
  triggerAdhocRule: func,
  includeDeletedStages: bool,
  appUser: object,
};

PathwayStagesTableCard.defaultProps = {
  includeDeletedStages: false,
};

export default PathwayStagesTableCard;
